import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Users, Plus, Ban, Play, Pencil, KeyRound, SlidersHorizontal, Download, History } from "lucide-react";
import { ACTIONS, WORKSPACES, type ActionAccess, type ActionId, type WorkspaceId } from "../../lib/workspaces";
import { toast } from "sonner";
import { buildWelcomePdf } from "../../lib/welcomePdf";
import { buildRedactedCredentialRecordPdf } from "../../lib/credentialRecordPdf";

const ROLES = ["owner_admin", "admin_support", "manager_trainer", "representative", "viewer"];
interface TenantUser { id: string; email: string; name: string; active: boolean; mfa_enabled: boolean; role?: string; worker_type?: WorkerType; workspace_access?: WorkspaceId[] | null; action_access?: ActionAccess | null }
interface Membership { user_id: string; tenant_role: string; active: boolean }
interface TemporaryLogin {
  id?: string; user_id?: string; email: string; name: string; temporary_password: string;
  tenant: { id: string; name: string; slug: string };
  credential_issuance: { id: string; version_number: number };
}
interface CredentialHistoryRow {
  id: string; user_id: string; recipient_name?: string | null; recipient_email?: string | null;
  version_number: number; reason?: string | null; status?: string | null;
  generated_by_name?: string | null; generated_by_scope?: string | null; generated_at?: string | null;
  download_count: number; first_downloaded_at?: string | null; last_downloaded_at?: string | null;
  activated_at?: string | null; redacted: true;
}
type WorkerType = "regular_employee" | "independent_contractor";
interface BillingProfile {
  worker_type: WorkerType; full_name: string; address: string; phone: string; billing_email: string;
  account_details: string; notes: string; default_currency: string; default_due_days: number;
  default_tax_name: string; default_tax_rate: number; default_base_compensation: number;
}
const emptyBillingProfile = (user?: TenantUser): BillingProfile => ({
  worker_type: "regular_employee", full_name: user?.name || "", address: "", phone: "",
  billing_email: user?.email || "", account_details: "", notes: "", default_currency: "CAD",
  default_due_days: 15, default_tax_name: "GST", default_tax_rate: 5, default_base_compensation: 0,
});

export default function TenantUsers() {
  const [rows, setRows] = useState<TenantUser[]>([]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<TenantUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetUser, setResetUser] = useState<TenantUser | null>(null);
  const [accessUser, setAccessUser] = useState<TenantUser | null>(null);
  const [workspaceAccess, setWorkspaceAccess] = useState<WorkspaceId[]>([]);
  const [actionAccess, setActionAccess] = useState<ActionAccess>({});
  const [temporaryLogin, setTemporaryLogin] = useState<TemporaryLogin | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState<CredentialHistoryRow[]>([]);
  const [historyUser, setHistoryUser] = useState<TenantUser | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "representative" });
  const [editForm, setEditForm] = useState({ email: "", name: "", role: "representative" });
  const [billingForm, setBillingForm] = useState<BillingProfile>(emptyBillingProfile());
  const [billingLoading, setBillingLoading] = useState(false);

  const load = async () => {
    try {
      const [{ data: users }, { data: memberships }, { data: contractors }] = await Promise.all([
        api.get<TenantUser[]>("/tenant/users"),
        api.get<Membership[]>("/tenant/memberships"),
        api.get<Array<{ id: string }>>("/tenant/staff-invoices/contractors"),
      ]);
      const roles = new Map((memberships || []).filter((m) => m.active).map((m) => [m.user_id, m.tenant_role]));
      const contractorIds = new Set((contractors || []).map((item) => item.id));
      const enriched = users.map((user) => ({ ...user, role: roles.get(user.id) || "viewer", worker_type: contractorIds.has(user.id) ? "independent_contractor" as const : "regular_employee" as const }));
      setRows(enriched);
    } catch (e) { toastError(e); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post<TemporaryLogin>("/tenant/users", form);
      toast.success("User created and temporary login generated");
      setOpen(false);
      setForm({ email: "", name: "", role: "representative" });
      setResetUser({ id: data.id || data.user_id || "", email: data.email, name: data.name, active: true, mfa_enabled: false, role: form.role });
      setTemporaryLogin(data);
      await load();
    } catch (e) { toastError(e); }
  };
  const reactivate = async (user: TenantUser) => {
    try {
      await api.post(`/tenant/users/${user.id}/reactivate`);
      toast.success("User reactivated");
      await load();
    } catch (e) { toastError(e); }
  };
  const resetLogin = async () => {
    if (!resetUser) return;
    try {
      const { data } = await api.post(`/tenant/users/${resetUser.id}/temporary-login`, { revoke_sessions: true });
      setTemporaryLogin(data);
      toast.success("Temporary login generated and existing sessions revoked");
    } catch (e) { toastError(e); }
  };
  const downloadLoginPdf = async () => {
    if (!temporaryLogin) return;
    const userId = temporaryLogin.user_id || temporaryLogin.id;
    if (!userId) return;
    const blob = buildWelcomePdf({
      tenantName: temporaryLogin.tenant.name,
      tenantSlug: temporaryLogin.tenant.slug,
      ownerName: temporaryLogin.name,
      ownerEmail: temporaryLogin.email,
      temporaryPassword: temporaryLogin.temporary_password,
      loginUrl: `${window.location.origin}/login`,
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${temporaryLogin.tenant.slug}-${temporaryLogin.email.split("@")[0]}-login-v${temporaryLogin.credential_issuance.version_number}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
    try {
      await api.post(`/tenant/users/${userId}/login-instructions/${temporaryLogin.credential_issuance.id}/downloaded`);
      toast.success("Login instructions PDF downloaded and recorded");
    } catch (e) { toastError(e); }
  };

  const openHistory = async (user?: TenantUser) => {
    setHistoryOpen(true); setHistoryUser(user || null); setHistoryLoading(true);
    try {
      const { data } = await api.get<CredentialHistoryRow[]>("/tenant/login-instructions-history");
      setHistoryRows(user ? data.filter((row) => row.user_id === user.id) : data);
    } catch (e) { toastError(e); setHistoryRows([]); } finally { setHistoryLoading(false); }
  };
  const downloadRedactedRecord = (row: CredentialHistoryRow) => {
    const blob = buildRedactedCredentialRecordPdf({
      recipientName: row.recipient_name, recipientEmail: row.recipient_email,
      versionNumber: row.version_number, reason: row.reason, status: row.status,
      generatedBy: row.generated_by_name || row.generated_by_scope,
      generatedAt: row.generated_at, downloadCount: row.download_count, activatedAt: row.activated_at,
    });
    const url = URL.createObjectURL(blob); const link = document.createElement("a");
    link.href = url; link.download = `login-instructions-${(row.recipient_email || row.user_id).replace(/[^a-z0-9]+/gi, "-")}-v${row.version_number}-redacted.pdf`;
    link.click(); URL.revokeObjectURL(url); toast.success("Redacted audit PDF downloaded");
  };

  const beginAccess = (user: TenantUser) => {
    setAccessUser(user);
    const visible = Array.isArray(user.workspace_access) ? user.workspace_access : WORKSPACES.map((item) => item.id);
    setWorkspaceAccess(visible);
    if (user.action_access && typeof user.action_access === "object") {
      setActionAccess(user.action_access);
    } else {
      setActionAccess(Object.fromEntries(WORKSPACES.map((item) => [item.id, ACTIONS.map((action) => action.id)])) as ActionAccess);
    }
  };
  const saveAccess = async () => {
    if (!accessUser) return;
    setSaving(true);
    try {
      await Promise.all([
        api.put(`/tenant/users/${accessUser.id}/workspace-access`, { workspace_ids: workspaceAccess }),
        api.put(`/tenant/users/${accessUser.id}/action-access`, { permissions: actionAccess }),
      ]);
      toast.success("Workspace and action access updated");
      setAccessUser(null);
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };
  const toggleWorkspace = (workspace: WorkspaceId) => {
    setWorkspaceAccess((current) => current.includes(workspace)
      ? current.filter((item) => item !== workspace)
      : [...current, workspace]);
  };
  const toggleAction = (workspace: WorkspaceId, action: ActionId) => {
    setActionAccess((current) => {
      const selected = current[workspace] || [];
      return {
        ...current,
        [workspace]: selected.includes(action)
          ? selected.filter((item) => item !== action)
          : [...selected, action],
      };
    });
  };
  const setViewOnly = (workspace: WorkspaceId) => {
    setWorkspaceAccess((current) => current.includes(workspace) ? current : [...current, workspace]);
    setActionAccess((current) => ({ ...current, [workspace]: ["view"] }));
  };

  const beginEdit = async (user: TenantUser) => {
    setEditing(user);
    setEditForm({ name: user.name || "", email: user.email || "", role: user.role || "viewer" });
    setBillingForm(emptyBillingProfile(user));
    setEditOpen(true);
    setBillingLoading(true);
    try {
      const { data } = await api.get<BillingProfile | null>("/tenant/staff-invoices/billing-profile", { params: { user_id: user.id } });
      if (data) setBillingForm({ ...emptyBillingProfile(user), ...data });
    } catch (e) { toastError(e); } finally { setBillingLoading(false); }
  };
  const saveEdit = async () => {
    if (!editing || !editForm.name.trim() || !editForm.email.trim() || saving) return;
    setSaving(true);
    try {
      await api.patch(`/tenant/users/${editing.id}/profile`, {
        name: editForm.name.trim(), email: editForm.email.trim(), role: editForm.role,
      });
      toast.success("User profile saved");
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const saveBilling = async () => {
    if (!editing || saving || billingLoading) return;
    setSaving(true);
    try {
      await api.put("/tenant/staff-invoices/billing-profile", {
        ...billingForm,
        full_name: billingForm.full_name.trim() || editForm.name.trim(),
        billing_email: billingForm.billing_email.trim() || editForm.email.trim(),
        default_currency: billingForm.default_currency.toUpperCase(),
      }, { params: { user_id: editing.id } });
      toast.success("Employment and billing details saved");
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Tenant Users" subtitle="Add and manage tenant users"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => openHistory()} data-testid="tenant-login-history"><History className="w-4 h-4 mr-1" /> Login history</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full" data-testid="create-user-btn"><Plus className="w-4 h-4 mr-1" /> New user</Button></DialogTrigger>
            <DialogContent data-testid="create-user-dialog">
              <DialogHeader><DialogTitle>New tenant user</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                <div><Label>Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={!form.email || !form.name.trim()} data-testid="user-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
        {rows.length === 0 ? <EmptyState icon={Users} title="No tenant users yet" /> : <>
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest"><tr><th className="text-left px-4 py-2.5 font-medium">Name</th><th className="text-left px-4 py-2.5 font-medium">Email</th><th className="text-left px-4 py-2.5 font-medium">Role</th><th className="text-left px-4 py-2.5 font-medium">Employment type</th><th className="text-left px-4 py-2.5 font-medium">Active</th><th className="text-left px-4 py-2.5 font-medium">MFA</th><th className="text-right px-4 py-2.5 font-medium">Actions</th></tr></thead>
              <tbody>{rows.map((user) => (
                <tr key={user.id} className="border-t border-slate-200" data-testid={`user-row-${user.id.slice(0,8)}`}>
                  <td className="px-4 py-2.5 font-medium">{user.name}</td><td className="px-4 py-2.5 text-slate-600">{user.email}</td><td className="px-4 py-2.5">{(user.role || "viewer").replace(/_/g, " ")}</td><td className="px-4 py-2.5">{user.worker_type === "independent_contractor" ? "Independent Contractor" : "Regular Employee"}</td><td className="px-4 py-2.5">{user.active ? "Yes" : <span className="text-rose-600">No</span>}</td><td className="px-4 py-2.5">{user.mfa_enabled ? "Yes" : "—"}</td>
                  <td className="px-4 py-2.5 text-right"><div className="inline-flex items-center gap-3">
                    <button className="text-xs text-sky-700 hover:underline" onClick={() => navigate(`/admin/users/${user.id}/360`)} data-testid={`view-360-${user.id.slice(0,8)}`}>View 360</button>
                    <button className="text-xs text-sky-700 inline-flex items-center gap-1 hover:underline" onClick={() => beginEdit(user)} data-testid={`edit-user-${user.id.slice(0,8)}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                    {user.role !== "owner_admin" && <button className="text-xs text-violet-700 inline-flex items-center gap-1 hover:underline" onClick={() => beginAccess(user)} data-testid={`manage-access-${user.id.slice(0,8)}`}><SlidersHorizontal className="w-3.5 h-3.5" /> Manage access</button>}
                    <button className="text-xs text-slate-700 inline-flex items-center gap-1 hover:underline" onClick={() => openHistory(user)} data-testid={`login-history-${user.id.slice(0,8)}`}><History className="w-3.5 h-3.5" /> Login history</button>
                    {user.active && <button className="text-xs text-amber-700 inline-flex items-center gap-1 hover:underline" onClick={() => { setResetUser(user); setTemporaryLogin(null); }}><KeyRound className="w-3.5 h-3.5" /> Reset login</button>}
                    {user.active ? <button className="text-xs text-rose-600 inline-flex items-center gap-1 hover:underline" onClick={() => navigate(`/admin/users/${user.id}/offboard`)} data-testid={`toggle-${user.id.slice(0,8)}`}><Ban className="w-3.5 h-3.5" /> Offboard</button> : <button className="text-xs text-emerald-600 inline-flex items-center gap-1 hover:underline" onClick={() => reactivate(user)} data-testid={`toggle-${user.id.slice(0,8)}`}><Play className="w-3.5 h-3.5" /> Reactivate</button>}
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>}
      </div>

      <Dialog open={!!accessUser} onOpenChange={(value) => { if (!value) setAccessUser(null); }}>
        <DialogContent className="max-w-[96vw] xl:max-w-7xl" data-testid="workspace-access-dialog">
          <DialogHeader><DialogTitle>Manage access — {accessUser?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Permissions can narrow this user's role, never expand it. View-only does not imply Add, Edit, Archive, Restore, Deactivate, or Delete.</p>
          <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[960px] text-sm" data-testid="action-access-matrix">
              <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500"><tr>
                <th className="px-3 py-3 text-left">Workspace</th>
                <th className="px-3 py-3 text-center">Visible</th>
                {ACTIONS.map((action) => <th key={action.id} className="px-3 py-3 text-center">{action.label}</th>)}
                <th className="px-3 py-3 text-center">Preset</th>
              </tr></thead>
              <tbody>{WORKSPACES.map((workspace) => (
                <tr key={workspace.id} className="border-t border-slate-200">
                  <td className="px-3 py-3 font-medium">{workspace.label}</td>
                  <td className="px-3 py-3 text-center"><input aria-label={`${workspace.label} visible`} type="checkbox" checked={workspaceAccess.includes(workspace.id)} onChange={() => toggleWorkspace(workspace.id)} data-testid={`workspace-${workspace.id}`} /></td>
                  {ACTIONS.map((action) => (
                    <td key={action.id} className="px-3 py-3 text-center">
                      <input aria-label={`${workspace.label} ${action.label}`} type="checkbox"
                        checked={(actionAccess[workspace.id] || []).includes(action.id)}
                        onChange={() => toggleAction(workspace.id, action.id)}
                        disabled={!workspaceAccess.includes(workspace.id)}
                        data-testid={`action-${workspace.id}-${action.id}`} />
                    </td>
                  ))}
                  <td className="px-3 py-3 text-center"><button type="button" className="text-xs text-sky-700 hover:underline" onClick={() => setViewOnly(workspace.id)}>View only</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-900"><strong>Delete is exceptional.</strong> Existing role rules and dependency checks still block unsafe deletion. Use Archive or Deactivate to preserve referrals, commissions, invoices, users, and audit history.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessUser(null)}>Cancel</Button>
            <Button onClick={saveAccess} disabled={saving} data-testid="save-workspace-access">{saving ? "Saving…" : "Save permissions"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" /></div>
            <div><Label>Role</Label><Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}><SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((role) => <SelectItem key={role} value={role}>{role.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select><p className="mt-1 text-xs text-slate-500">Changing a role affects access immediately. The last active owner cannot be demoted.</p></div>
            <div><Label>Employment type</Label><Select value={billingForm.worker_type} onValueChange={(v: WorkerType) => setBillingForm({ ...billingForm, worker_type: v })} disabled={billingLoading}><SelectTrigger data-testid="edit-worker-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="regular_employee">Regular Employee</SelectItem><SelectItem value="independent_contractor">Independent Contractor</SelectItem></SelectContent></Select><p className="mt-1 text-xs text-slate-500">Only Independent Contractors can create new staff invoices. Historical invoices are always preserved.</p></div>
            {billingForm.worker_type === "independent_contractor" && <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3" data-testid="owner-billing-profile">
              <div><Label>Legal / billing name</Label><Input value={billingForm.full_name} onChange={(e) => setBillingForm({ ...billingForm, full_name: e.target.value })} /></div>
              <div><Label>Billing email</Label><Input type="email" value={billingForm.billing_email} onChange={(e) => setBillingForm({ ...billingForm, billing_email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={billingForm.phone} onChange={(e) => setBillingForm({ ...billingForm, phone: e.target.value })} /></div>
              <div><Label>Currency</Label><Input maxLength={3} value={billingForm.default_currency} onChange={(e) => setBillingForm({ ...billingForm, default_currency: e.target.value })} /></div>
              <div className="col-span-2"><Label>Billing address</Label><Input value={billingForm.address} onChange={(e) => setBillingForm({ ...billingForm, address: e.target.value })} /></div>
              <div className="col-span-2"><Label>Account / payment details</Label><Input value={billingForm.account_details} onChange={(e) => setBillingForm({ ...billingForm, account_details: e.target.value })} /></div>
              <div><Label>Payment terms (days)</Label><Input type="number" min={0} max={180} value={billingForm.default_due_days} onChange={(e) => setBillingForm({ ...billingForm, default_due_days: Number(e.target.value) })} /></div>
              <div><Label>Base compensation</Label><Input type="number" min={0} step="0.01" value={billingForm.default_base_compensation} onChange={(e) => setBillingForm({ ...billingForm, default_base_compensation: Number(e.target.value) })} /></div>
              <div><Label>Tax name</Label><Input value={billingForm.default_tax_name} onChange={(e) => setBillingForm({ ...billingForm, default_tax_name: e.target.value })} /></div>
              <div><Label>Tax rate (%)</Label><Input type="number" min={0} max={100} step="0.01" value={billingForm.default_tax_rate} onChange={(e) => setBillingForm({ ...billingForm, default_tax_rate: Number(e.target.value) })} /></div>
              <div className="col-span-2"><Label>Private billing notes</Label><Input value={billingForm.notes} onChange={(e) => setBillingForm({ ...billingForm, notes: e.target.value })} /></div>
            </div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Close</Button><Button variant="outline" onClick={saveBilling} disabled={saving || billingLoading} data-testid="save-billing-btn">{saving ? "Saving…" : "Save employment & billing"}</Button><Button onClick={saveEdit} disabled={saving || !editForm.name.trim() || !editForm.email.trim()} data-testid="save-user-btn">{saving ? "Saving…" : "Save user profile"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-5xl" data-testid="login-history-dialog">
          <DialogHeader><DialogTitle>{historyUser ? `Login instruction history — ${historyUser.name}` : "Tenant login instruction history"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Permanent records are redacted. To obtain a usable password, generate a new temporary login; the previous credential is revoked and remains in this register.</p>
          <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200">
            {historyLoading ? <p className="p-6 text-sm text-slate-500">Loading history…</p> : historyRows.length === 0 ? <p className="p-6 text-sm text-slate-500">No login instructions have been issued yet.</p> : <table className="w-full min-w-[900px] text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-3 py-2 text-left">Recipient</th><th className="px-3 py-2 text-left">Version</th><th className="px-3 py-2 text-left">Reason</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Generated</th><th className="px-3 py-2 text-left">By</th><th className="px-3 py-2 text-left">Downloads</th><th className="px-3 py-2 text-right">Record</th></tr></thead>
              <tbody>{historyRows.map((row) => <tr key={row.id} className="border-t border-slate-200"><td className="px-3 py-2"><div className="font-medium">{row.recipient_name || "—"}</div><div className="text-xs text-slate-500">{row.recipient_email}</div></td><td className="px-3 py-2">v{row.version_number}</td><td className="px-3 py-2 capitalize">{(row.reason || "—").replace(/_/g, " ")}</td><td className="px-3 py-2 capitalize">{(row.status || "—").replace(/_/g, " ")}</td><td className="px-3 py-2">{row.generated_at ? new Date(row.generated_at).toLocaleString() : "—"}</td><td className="px-3 py-2">{row.generated_by_name || row.generated_by_scope || "—"}</td><td className="px-3 py-2">{row.download_count || 0}</td><td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => downloadRedactedRecord(row)}><Download className="w-3.5 h-3.5 mr-1" /> Redacted PDF</Button></td></tr>)}</tbody>
            </table>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(value) => { if (!value) { setResetUser(null); setTemporaryLogin(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{temporaryLogin ? `Login instructions for ${resetUser?.name}` : `Reset login for ${resetUser?.name}`}</DialogTitle></DialogHeader>
          {!temporaryLogin ? <p className="text-sm text-slate-600">This generates a one-time temporary password, forces a password change at next login, and revokes all existing sessions. The old password is never revealed.</p> : <div className="rounded-lg border bg-slate-50 p-4 text-sm space-y-2"><div><span className="text-slate-500">Email:</span> {temporaryLogin.email}</div><div data-testid="staff-temporary-password"><span className="text-slate-500">Temporary password:</span> <span className="font-mono font-semibold">{temporaryLogin.temporary_password}</span></div><p className="text-amber-700">Copy this now. It will not be displayed again after this window closes.</p></div>}
          <DialogFooter>{temporaryLogin ? <>
            <Button variant="outline" onClick={downloadLoginPdf} data-testid="download-staff-login-pdf"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
            <Button onClick={() => navigator.clipboard.writeText(`RemediKeep login\nEmail: ${temporaryLogin.email}\nTemporary password: ${temporaryLogin.temporary_password}\nYou must change this password after signing in.`)}>Copy login instructions</Button>
          </> : <Button onClick={resetLogin}><KeyRound className="w-4 h-4 mr-1" /> Generate temporary login</Button>}</DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
